import { ClientApi } from '../../api/client-api';
import { customElement } from 'lit/decorators.js';
import { EventContactOpen } from '../data/events';
import { EventSnippet } from '../../../webmodule-common/other/ui/events';
import { getApiFactory } from '../../api/api-injector';
import { html } from 'lit';
import { DataTableWrapper } from '../../../webmodule-common/other/ui/datatable-view';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { resourceClient } from '../../dealer-franchisee/clients/ui/launcher';
import { LangContactListTable } from '../../../localization/scripts/clients/views/contact-list-table-lang';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store';
import { ViewContactSummary } from '../../api/dealer-api-interface-client';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';

interface ContactSummaryTableBaseOptions {
  title: EventSnippet;
}

@customElement('wm-contactsummarytablebase')
class ContactSummaryTableBase extends DataTableWrapper<ViewContactSummary> {
  eventTitle: EventSnippet;
  api: ClientApi = getApiFactory().client();
  filter: string | null;
  clientIdFilter: string | null;

  constructor(options: ContactSummaryTableBaseOptions) {
    super();
    this.eventTitle = options.title;
    this.filter = null;
    this.clientIdFilter = null;
  }

  enableFiltering(): boolean {
    return true;
  }

  updateFilter(_searchTerm: string | null) {
    this.filter = _searchTerm;
  }

  updateClientFilter(_clientId: string | null) {
    this.clientIdFilter = _clientId;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<ViewContactSummary>> {
    const results = await this.api.browseContactSummary({
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      sortField: request.sortField,
      sortAsc: !request.sortAsc,
      clientOwnerId: userDataStore.clientOwnerId,
      filter: this.filter,
      clientId: this.clientIdFilter
    });
    if (!results)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    return results.contactSummary;
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangContactListTable.contact(),
        fieldName: 'name',
        minWidth: '180px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;
          return html`${rowItem.name}`;
        }
      },
      {
        title: () => LangContactListTable.primary(),
        fieldName: 'isPrimary',
        width: '90px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;
          return this.isPrimary(rowItem.isPrimary);
        }
      },
      {
        title: () => LangContactListTable.client(),
        fieldName: 'clientName',
        sortable: true,
        minWidth: '180px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;
          return html`${rowItem.clientName}`;
        }
      },
      {
        title: () => LangContactListTable.email(),
        fieldName: 'email',
        minWidth: '200px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;
          return html`${rowItem.email}`;
        }
      },
      {
        title: () => LangContactListTable.role(),
        fieldName: 'title',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;
          return html`${rowItem.title}`;
        }
      },
      {
        title: () => LangContactListTable.phone(),
        fieldName: 'mobile',
        minWidth: '140px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;
          return html`${rowItem.mobile}`;
        }
      }
    ];
  }

  protected isPrimary(data: boolean) {
    return data ? html` <webmodule-icon library="fa" name="far-circle-check"></webmodule-icon>` : '';
  }
}

interface ContactSummaryTableOptions extends ContactSummaryTableBaseOptions {
  openContactEvent: EventContactOpen;
  addButtonTitle: EventSnippet;
  pageFragment: string;
}

@customElement('wm-contactsummarytable')
export class ContactSummaryTable extends ContactSummaryTableBase {
  openContactEvent: EventContactOpen;
  addButtonTitle: EventSnippet;
  pageFragment: string;

  constructor(options: ContactSummaryTableOptions) {
    super({ title: options.title });
    this.openContactEvent = options.openContactEvent;
    this.addButtonTitle = options.addButtonTitle;
    this.pageFragment = options.pageFragment;
  }

  // Full-page tab table: fill the panel and keep the paginator pinned to the
  // bottom instead of floating under the rows. (The picker subclass of the base
  // stays content-height via the default.)
  override fillHeight(): boolean {
    return true;
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangContactListTable.contact(),
        fieldName: 'id',
        minWidth: '180px',
        sortable: true,
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`<a class="contact-link" data-contact-id=${rowItem.id} href="#">${rowItem.name}</a>`;
        },
        click: (e: Event, _table: WebModuleTable, item: unknown) => {
          e.preventDefault();
          e.stopImmediatePropagation();

          const rowItem = item as ViewContactSummary;

          this.eventHandler(async () => {
            await this.openContact(rowItem);
          });

          return true;
        }
      },
      {
        title: () => LangContactListTable.primary(),
        fieldName: 'isPrimary',
        width: '90px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return this.isPrimary(rowItem.isPrimary);
        }
      },
      {
        title: () => LangContactListTable.client(),
        fieldName: 'clientId',
        sortable: true,
        minWidth: '180px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`<a
            class="client-link"
            data-contact-id=${rowItem.clientId}
            href="${resolveURL(resourceClient, rowItem.clientId)}"
            >${rowItem.clientName}</a
          >`;
        }
      },
      {
        title: () => LangContactListTable.email(),
        fieldName: 'email',
        minWidth: '200px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`${rowItem.email}`;
        }
      },
      {
        title: () => LangContactListTable.role(),
        fieldName: 'title',
        minWidth: '150px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`${rowItem.title}`;
        }
      },
      {
        title: () => LangContactListTable.phone(),
        fieldName: 'mobile',
        minWidth: '140px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`${rowItem.mobile}`;
        }
      }
    ];
  }

  public async openContact(viewContactSummary: ViewContactSummary): Promise<void> {
    await this.openContactEvent?.(viewContactSummary);
  }
}

//We can extend this to add options like pick immediately, and what buttons to show.
interface ContactSummaryPickerOptions extends ContactSummaryTableBaseOptions {
  clientId?: string;
  onContactSelect?: (contactSummary: ViewContactSummary) => void;
}

@customElement('wm-contactsummarypickerbrowser')
export class ContactSummaryPickerBrowser extends ContactSummaryTableBase {
  onContactSelect?: (contactSummary: ViewContactSummary) => void;

  constructor(options: ContactSummaryPickerOptions) {
    super({ title: options.title });
    this.clientIdFilter = options.clientId ?? null;
    this.onContactSelect = options.onContactSelect;
    this.clickrows = true;
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangContactListTable.contact(),
        fieldName: 'id',
        minWidth: '180px',
        sortable: true,
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`<span class="contact-link" data-contact-id=${rowItem.id}>${rowItem.name}</span>`;
        }
      },
      {
        title: () => LangContactListTable.primary(),
        fieldName: 'isPrimary',
        width: '90px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return this.isPrimary(rowItem.isPrimary);
        }
      },
      {
        title: () => LangContactListTable.client(),
        fieldName: 'clientName',
        minWidth: '180px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as ViewContactSummary;

          return html`<span class="client-link" data-contact-id=${rowItem.clientId}>${rowItem.clientName}</span>`;
        }
      }
    ];
  }

  async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: ViewContactSummary }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();

    this.eventHandler(async () => {
      if (this.onContactSelect) this.onContactSelect?.(e.detail.item);
    });
  }
}
